import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  TablePagination,
  Skeleton,
  Alert,
  Stack,
  useTheme,
  alpha,
  TextField,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Search, X } from 'lucide-react';
import axios from '../utils/axios';
import PageHeader from '../components/common/PageHeader';
import ExportTableButton from '../components/common/ExportTableButton';

const StatCard = ({ label, value, color = '#FE5F01' }) => {
  const theme = useTheme();
  return (
    <Card
      sx={{
        borderRadius: 3,
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: `0 1px 2px ${alpha(theme.palette.grey[900], 0.04)}`,
        transition: 'transform 160ms ease, box-shadow 160ms ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 12px 24px ${alpha(color, 0.12)}`,
        },
      }}
    >
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 700,
            color: 'text.secondary',
            textTransform: 'uppercase',
            letterSpacing: 0.6,
          }}
        >
          {label}
        </Typography>
        <Typography
          sx={{
            fontSize: { xs: '1.5rem', md: '1.85rem' },
            fontWeight: 800,
            color: 'text.primary',
            mt: 0.5,
            lineHeight: 1.1,
          }}
        >
          {value ?? 0}
        </Typography>
      </CardContent>
    </Card>
  );
};

const TabRows = ({ endpoint, columns }) => {
  const theme = useTheme();
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  // Debounce search input automatically
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(0);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  const load = async (signal) => {
    setLoading(true);
    setErr('');
    try {
      const { data } = await axios.get(endpoint, {
        params: { page: page + 1, limit: rows, search: debouncedSearch || undefined },
        signal,
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') return;
      setErr(e.response?.data?.msg || e.message);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, page, rows, debouncedSearch]);

  return (
    <Box>
      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}

      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ maxWidth: 320, width: '100%' }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search records..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={16} />
                  </InputAdornment>
                ),
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearch('')}>
                      <X size={14} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              },
            }}
          />
        </Box>
        <ExportTableButton
          data={items}
          total={total}
          onExportAll={async () => {
            const { data } = await axios.get(endpoint, {
              params: { all: true, search: debouncedSearch || undefined },
            });
            return data.items || [];
          }}
          columns={columns.map(c => ({
            label: c.label,
            key: c.key,
            value: (r) => {
              if (c.render) {
                const res = c.render(r);
                if (typeof res === 'string' || typeof res === 'number') return res;
              }
              const val = r[c.key];
              if (val === undefined || val === null) return '';
              if (typeof val === 'object') return JSON.stringify(val);
              return val;
            }
          }))}
          filename="automation_logs"
        />
      </Box>

      {loading ? (
        <TableContainer
          sx={{
            overflowX: 'auto',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: '12px',
          }}
        >
          <Table sx={{ minWidth: 800 }}>
            <TableHead sx={{ bgcolor: alpha(theme.palette.grey[500], 0.06) }}>
              <TableRow>
                {columns.map((c) => (
                  <TableCell
                    key={c.key}
                    sx={{
                      fontWeight: 800,
                      color: 'text.secondary',
                      fontSize: 11,
                      textTransform: 'uppercase',
                      letterSpacing: 0.8,
                      py: 1.75,
                      px: 2.5,
                      borderBottom: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    {c.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.from({ length: 6 }).map((_, i) => (
                <TableRow key={i}>
                  {columns.map((c, j) => (
                    <TableCell key={j} sx={{ py: 2, px: 2.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
                      <Skeleton variant="text" width={j === 0 ? '75%' : '45%'} height={24} sx={{ borderRadius: 1 }} />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <TableContainer
          sx={{
            overflowX: 'auto',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: '12px',
            '& th:first-of-type': {
              borderTopLeftRadius: '12px',
            },
            '& th:last-of-type': {
              borderTopRightRadius: '12px',
            },
          }}
        >
          <Table sx={{ minWidth: 800 }}>
            <TableHead sx={{ bgcolor: alpha(theme.palette.grey[500], 0.06) }}>
              <TableRow>
                {columns.map((c) => (
                  <TableCell
                    key={c.key}
                    sx={{
                      fontWeight: 800,
                      color: 'text.secondary',
                      fontSize: 11,
                      textTransform: 'uppercase',
                      letterSpacing: 0.8,
                      py: 1.75,
                      px: 2.5,
                      borderBottom: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    {c.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {items.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    align="center"
                    sx={{ py: 6, px: 2.5, borderBottom: 'none' }}
                  >
                    <Box sx={{ py: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <Box sx={{ width: 48, height: 48, borderRadius: '50%', bgcolor: alpha(theme.palette.grey[500], 0.10), color: 'text.secondary', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5 }}>
                        <Search size={22} />
                      </Box>
                      <Typography sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5, fontSize: 15 }}>No records found</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 360, fontSize: 13 }}>
                        There are no matching entries for this section right now.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                items.map((it) => (
                  <TableRow key={it._id} hover sx={{ '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.02) } }}>
                    {columns.map((c) => (
                      <TableCell
                        key={c.key}
                        sx={{
                          py: 1.75,
                          px: 2.5,
                          color: 'text.primary',
                          fontSize: '0.875rem',
                          borderBottom: `1px solid ${theme.palette.divider}`,
                        }}
                      >
                        {c.render ? c.render(it) : (it[c.key] ?? '—')}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', pt: 1 }}>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rows}
          onRowsPerPageChange={(e) => { setRows(Number(e.target.value)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{
            border: 'none',
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              fontWeight: 600,
              color: 'text.secondary',
            },
          }}
        />
      </Box>
    </Box>
  );
};

const AutomationView = () => {
  const theme = useTheme();
  const [tab, setTab] = useState(0);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    axios.get('/admin/automation/summary').then((r) => setSummary(r.data.summary)).catch(() => {});
  }, []);

  const fmtDate = (d) => d ? new Date(d).toLocaleString() : '—';
  const fullName = (u) => u ? `${u.first_name || ''} ${u.last_name || ''}`.trim() : '—';

  const LEAD_STATUS_CONFIG = {
    new: { bg: alpha('#8b5cf6', 0.14), color: '#7c3aed', label: 'New' },
    contacted: { bg: alpha('#3b82f6', 0.14), color: '#2563eb', label: 'Contacted' },
    negotiating: { bg: alpha('#f59e0b', 0.14), color: '#d97706', label: 'Negotiating' },
    won: { bg: alpha('#10b981', 0.14), color: '#059669', label: 'Won' },
    lost: { bg: alpha('#ef4444', 0.14), color: '#dc2626', label: 'Lost' },
  };

  const JOB_STATUS_CONFIG = {
    completed: { bg: alpha('#10b981', 0.14), color: '#059669' },
    processing: { bg: alpha('#8b5cf6', 0.14), color: '#7c3aed' },
    pending: { bg: alpha('#f59e0b', 0.14), color: '#d97706' },
    failed: { bg: alpha('#ef4444', 0.14), color: '#dc2626' },
  };

  const TIER_COLORS = {
    Gold: { bg: alpha('#f59e0b', 0.15), color: '#b45309' },
    Silver: { bg: alpha('#64748b', 0.15), color: '#334155' },
    Bronze: { bg: alpha('#d97706', 0.15), color: '#92400e' },
  };

  const tabs = [
    {
      label: 'Wishlists',
      endpoint: '/admin/automation/wishlists',
      columns: [
        {
          key: 'user_id',
          label: 'User',
          render: (it) => (
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: 13 }}>{fullName(it.user_id)}</Typography>
              <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{it.user_id?.email || ''}</Typography>
            </Box>
          ),
        },
        { key: 'product_id', label: 'Product', render: (it) => <Typography sx={{ fontWeight: 700, fontSize: 13, color: 'primary.main' }}>{it.product_id?.product_name || '—'}</Typography> },
        { key: 'created_at', label: 'Saved at', render: (it) => fmtDate(it.created_at) },
      ],
    },
    {
      label: 'Saved Searches',
      endpoint: '/admin/automation/saved-searches',
      columns: [
        {
          key: 'user_id',
          label: 'User',
          render: (it) => (
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: 13 }}>{fullName(it.user_id)}</Typography>
              <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{it.user_id?.email || ''}</Typography>
            </Box>
          ),
        },
        { key: 'name', label: 'Name', render: (it) => <Typography sx={{ fontWeight: 700, fontSize: 13 }}>{it.name}</Typography> },
        { key: 'category', label: 'Category', render: (it) => <Chip size="small" label={it.category || 'All'} sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: alpha('#3b82f6', 0.12), color: '#2563eb' }} /> },
        { key: 'city', label: 'City' },
        { key: 'alert_frequency', label: 'Frequency', render: (it) => <Chip size="small" label={it.alert_frequency} sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: alpha('#8b5cf6', 0.12), color: '#7c3aed' }} /> },
        { key: 'last_match_count', label: 'Last matches', render: (it) => <Typography sx={{ fontWeight: 700 }}>{it.last_match_count ?? 0}</Typography> },
      ],
    },
    {
      label: 'RFQs',
      endpoint: '/admin/automation/rfqs',
      columns: [
        { key: 'title', label: 'Title', render: (it) => <Typography sx={{ fontWeight: 700, fontSize: 13 }}>{it.title}</Typography> },
        { key: 'buyer_name', label: 'Buyer' },
        { key: 'category', label: 'Category', render: (it) => <Chip size="small" label={it.category || 'General'} sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: alpha('#6366f1', 0.12), color: '#6366f1' }} /> },
        { key: 'city', label: 'City' },
        { key: 'quantity', label: 'Qty', render: (it) => <Typography sx={{ fontWeight: 700 }}>{it.quantity}</Typography> },
        {
          key: 'status',
          label: 'Status',
          render: (it) => (
            <Chip
              size="small"
              label={it.status}
              sx={{
                height: 22,
                fontSize: 11,
                fontWeight: 800,
                bgcolor: it.status === 'closed' ? alpha('#64748b', 0.14) : alpha('#10b981', 0.14),
                color: it.status === 'closed' ? '#475569' : '#059669',
              }}
            />
          ),
        },
        { key: 'quotes_count', label: 'Quotes', render: (it) => <Chip size="small" label={it.quotes_count ?? 0} sx={{ fontWeight: 700 }} /> },
        { key: 'created_at', label: 'Created', render: (it) => fmtDate(it.created_at) },
      ],
    },
    {
      label: 'Bulk Uploads',
      endpoint: '/admin/automation/bulk-jobs',
      columns: [
        {
          key: 'user_id',
          label: 'Seller',
          render: (it) => (
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: 13 }}>{fullName(it.user_id)}</Typography>
              <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{it.user_id?.email || ''}</Typography>
            </Box>
          ),
        },
        { key: 'file_name', label: 'File', render: (it) => <Typography sx={{ fontWeight: 600, fontSize: 13 }}>{it.file_name}</Typography> },
        { key: 'total_rows', label: 'Rows', render: (it) => <Typography sx={{ fontWeight: 700 }}>{it.total_rows}</Typography> },
        { key: 'success_count', label: 'OK', render: (it) => <Chip size="small" color="success" label={it.success_count} sx={{ height: 20, fontWeight: 800 }} /> },
        { key: 'failed_count', label: 'Fail', render: (it) => <Chip size="small" color={it.failed_count > 0 ? 'error' : 'default'} label={it.failed_count} sx={{ height: 20, fontWeight: 800 }} /> },
        {
          key: 'status',
          label: 'Status',
          render: (it) => {
            const st = JOB_STATUS_CONFIG[it.status?.toLowerCase()] || JOB_STATUS_CONFIG.processing;
            return <Chip size="small" label={it.status} sx={{ height: 22, fontSize: 11, fontWeight: 800, bgcolor: st.bg, color: st.color, textTransform: 'capitalize' }} />;
          },
        },
        { key: 'created_at', label: 'When', render: (it) => fmtDate(it.created_at) },
      ],
    },
    {
      label: 'Conversations',
      endpoint: '/admin/automation/conversations',
      columns: [
        { key: 'buyer_id', label: 'Buyer', render: (it) => <Typography sx={{ fontWeight: 700, fontSize: 13 }}>{fullName(it.buyer_id)}</Typography> },
        { key: 'seller_id', label: 'Seller', render: (it) => <Typography sx={{ fontWeight: 700, fontSize: 13 }}>{fullName(it.seller_id)}</Typography> },
        { key: 'product_id', label: 'Product', render: (it) => <Typography sx={{ fontWeight: 700, fontSize: 13, color: 'primary.main' }}>{it.product_id?.product_name || '—'}</Typography> },
        { key: 'last_message', label: 'Last message', render: (it) => <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>{(it.last_message || '').slice(0, 60)}</Typography> },
        { key: 'last_message_at', label: 'Last at', render: (it) => fmtDate(it.last_message_at) },
      ],
    },
    {
      label: 'Lead Pipeline',
      endpoint: '/admin/automation/leads',
      columns: [
        { key: 'phone', label: 'Phone', render: (it) => <Typography sx={{ fontWeight: 700, fontSize: 13 }}>{it.phone || '—'}</Typography> },
        { key: 'category_name', label: 'Category', render: (it) => <Chip size="small" label={it.category_name || 'General'} sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: alpha('#6366f1', 0.12), color: '#6366f1' }} /> },
        { key: 'city', label: 'City', render: (it) => <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{it.city || '—'}</Typography> },
        { key: 'seller_id', label: 'Seller', render: (it) => <Typography sx={{ fontWeight: 700, fontSize: 13 }}>{fullName(it.seller_id)}</Typography> },
        {
          key: 'status',
          label: 'Status',
          render: (it) => {
            const st = LEAD_STATUS_CONFIG[it.status?.toLowerCase()] || LEAD_STATUS_CONFIG.new;
            return <Chip size="small" label={st.label} sx={{ height: 22, fontSize: 11, fontWeight: 800, bgcolor: st.bg, color: st.color }} />;
          },
        },
        {
          key: 'is_read',
          label: 'Read',
          render: (it) => (
            <Chip
              size="small"
              label={it.is_read ? 'Read' : 'Unread'}
              sx={{
                height: 20,
                fontSize: 10,
                fontWeight: 800,
                bgcolor: it.is_read ? alpha('#10b981', 0.14) : alpha('#f59e0b', 0.14),
                color: it.is_read ? '#059669' : '#d97706',
              }}
            />
          ),
        },
        { key: 'created_at', label: 'Created', render: (it) => fmtDate(it.created_at) },
      ],
    },
    {
      label: 'Referral Leaders',
      endpoint: '/admin/automation/referrals/leaders',
      columns: [
        {
          key: 'first_name',
          label: 'User',
          render: (it) => (
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: 13 }}>{fullName(it)}</Typography>
              <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{it.email || ''}</Typography>
            </Box>
          ),
        },
        {
          key: 'referralTier',
          label: 'Tier',
          render: (it) => {
            const tier = TIER_COLORS[it.referralTier] || TIER_COLORS.Silver;
            return <Chip size="small" label={it.referralTier || 'Standard'} sx={{ height: 22, fontSize: 11, fontWeight: 800, bgcolor: tier.bg, color: tier.color }} />;
          },
        },
        { key: 'successfulReferrals', label: 'Successful', render: (it) => <Chip size="small" color="success" label={it.successfulReferrals ?? 0} sx={{ fontWeight: 800 }} /> },
        { key: 'referralCount', label: 'Total invites', render: (it) => <Typography sx={{ fontWeight: 700 }}>{it.referralCount ?? 0}</Typography> },
        { key: 'wallet', label: 'Wallet', render: (it) => <Typography sx={{ fontWeight: 800, color: '#059669' }}>₹{Number(it.wallet || 0).toLocaleString('en-IN')}</Typography> },
      ],
    },
  ];

  const getTabCount = (tabIndex) => {
    if (!summary) return null;
    switch (tabIndex) {
      case 0: return summary.wishlists;
      case 1: return summary.saved_searches;
      case 2: return summary.rfqs;
      case 3: return summary.bulk_uploads;
      case 4: return summary.conversations;
      case 5: return Object.values(summary.leads_by_status || {}).reduce((a, b) => a + b, 0);
      default: return null;
    }
  };

  return (
    <Box sx={{ py: 1, width: '100%' }}>
      <PageHeader
        title="Automation Data"
        subtitle="Wishlists, saved searches, RFQs, bulk uploads, chats, lead pipeline, and referrals"
        breadcrumbs={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'Operations', href: '/automation' },
          { label: 'Automation Data' }
        ]}
      />

      {summary && (
        <Grid container spacing={2.5} sx={{ width: '100%', mt: { xs: 0.5, md: 1 }, mb: 4 }}>
          <Grid size={{ xs: 6, md: 3 }}><StatCard label="Wishlists" value={summary.wishlists} color="#f43f5e" /></Grid>
          <Grid size={{ xs: 6, md: 3 }}><StatCard label="Saved Searches" value={summary.saved_searches} color="#6366f1" /></Grid>
          <Grid size={{ xs: 6, md: 3 }}><StatCard label="RFQs / Quotes" value={`${summary.rfqs}/${summary.quotes}`} color="#f59e0b" /></Grid>
          <Grid size={{ xs: 6, md: 3 }}><StatCard label="Bulk Uploads" value={summary.bulk_uploads} color="#16a34a" /></Grid>
          <Grid size={{ xs: 6, md: 3 }}><StatCard label="Conversations" value={summary.conversations} color="#6366f1" /></Grid>
          <Grid size={{ xs: 6, md: 3 }}><StatCard label="Messages" value={summary.messages} color="#0ea5e9" /></Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card
              sx={{
                borderRadius: 3,
                border: `1px solid ${theme.palette.divider}`,
                boxShadow: `0 1px 2px ${alpha(theme.palette.grey[900], 0.04)}`,
                height: '100%',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <CardContent sx={{ p: 2.5, width: '100%' }}>
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    letterSpacing: 0.6,
                    mb: 1.5,
                  }}
                >
                  Lead Pipeline
                </Typography>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    overflowX: 'auto',
                    width: '100%',
                    pb: 0.5,
                    '&::-webkit-scrollbar': { display: 'none' },
                    msOverflowStyle: 'none',
                    scrollbarWidth: 'none',
                  }}
                >
                  {Object.entries(summary.leads_by_status || {}).map(([k, v]) => (
                    <Chip
                      key={k}
                      label={`${k.toUpperCase()}: ${v}`}
                      size="small"
                      sx={{
                        fontWeight: 700,
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                        color: 'primary.main',
                        borderRadius: 1,
                        flexShrink: 0,
                      }}
                    />
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Card sx={{ borderRadius: 4, border: `1px solid ${theme.palette.divider}`, overflow: 'hidden', mb: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            borderBottom: `1px solid ${theme.palette.divider}`,
            bgcolor: alpha(theme.palette.grey[500], 0.04),
            px: 1,
            '& .MuiTab-root': {
              fontWeight: 700,
              textTransform: 'none',
              fontSize: 14,
              py: 1.75,
              minWidth: 'auto',
              px: { xs: 2, sm: 2.5 },
              color: 'text.secondary',
              '&.Mui-selected': {
                color: 'primary.main',
                fontWeight: 800,
              },
            },
          }}
        >
          {tabs.map((t, i) => {
            const count = getTabCount(i);
            return (
              <Tab
                key={i}
                label={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <span>{t.label}</span>
                    {count !== null && count !== undefined && (
                      <Chip
                        size="small"
                        label={count}
                        sx={{
                          height: 18,
                          fontSize: 11,
                          fontWeight: 800,
                          bgcolor: tab === i ? alpha(theme.palette.primary.main, 0.15) : alpha(theme.palette.grey[500], 0.12),
                          color: tab === i ? 'primary.main' : 'text.secondary',
                        }}
                      />
                    )}
                  </Stack>
                }
              />
            );
          })}
        </Tabs>
        <Box p={{ xs: 2.5, md: 3 }}>
          <TabRows endpoint={tabs[tab].endpoint} columns={tabs[tab].columns} />
        </Box>
      </Card>
    </Box>
  );
};

export default AutomationView;
